import { useQuery } from "@tanstack/react-query";
import { SearchXIcon } from "lucide-react";
import { Link, useParams } from "react-router";
import { EmptyState, ErrorState } from "@/components/common/states.tsx";
import { RightPanelSkeleton } from "@/components/panel/right-panel.tsx";
import { SessionView } from "@/components/session/session-view.tsx";
import { NavigationButton } from "@/components/shell/shell-context.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { isNotFound, useIsWide } from "@/lib/hooks.ts";
import { useDocumentTitle } from "@/lib/queries.ts";

/** The loading layout of a session: header, a user bubble and a turn card, and the panel. */
export function SessionSkeleton() {
  const isWide = useIsWide();
  return (
    <div className="flex h-full min-h-0" aria-busy>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-14 shrink-0 items-center gap-3 border-b px-4">
          <NavigationButton />
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 px-6 pt-6">
          <Skeleton className="ml-auto h-14 w-2/3 rounded-2xl" />
          <div className="rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="size-5 rounded" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="ml-auto h-6 w-32 rounded-full" />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
            <Skeleton className="mt-4 h-4 w-48" />
            <div className="mt-4 flex flex-col gap-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-11/12" />
              <Skeleton className="h-3.5 w-4/5" />
            </div>
          </div>
        </div>
      </div>
      {isWide ? (
        <aside className="w-[400px] shrink-0 border-l bg-panel">
          <RightPanelSkeleton />
        </aside>
      ) : null}
    </div>
  );
}

export function SessionPage() {
  const { sessionId = "" } = useParams();
  const { t } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.session(sessionId),
    queryFn: () => api.session(sessionId),
    enabled: sessionId !== "",
  });
  useDocumentTitle(query.data?.session.title || null, t.brand.name);

  if (query.isPending) return <SessionSkeleton />;
  if (query.isError) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 items-center border-b px-3 xl:hidden">
          <NavigationButton />
        </div>
        <div className="flex flex-1 items-center justify-center">
          {isNotFound(query.error) ? (
            <EmptyState
              icon={SearchXIcon}
              title={t.errors.sessionNotFound}
              text={t.errors.sessionNotFoundText}
              action={
                <Button asChild variant="outline" size="sm">
                  <Link to="/app">{t.errors.toApp}</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          )}
        </div>
      </div>
    );
  }
  return <SessionView key={sessionId} detail={query.data} queryKey={queryKeys.session(sessionId)} />;
}
